import { ref } from 'vue';
import { defineStore } from 'pinia';
import { CaseService, EntryService, StudyService } from '@/services';
import { TableColumnType, type TableColumnModel } from '@/components';
import { LoadingStateModel, ArchiveModel, type PostLockUnlockResource } from '@/models';
import { useFormStore } from '@/stores/forms';
import { useStudyStore } from '@/stores/study';


export const useArchiveStore = defineStore('archive', () => {

  const tableColumns: TableColumnModel[] = [
    {
      id: 1,
      header: 'Type',
      key: 'label',
      type: TableColumnType.TEXT_WTIH_ICON,
      sortable: true
    },
    {
      id: 2,
      header: 'Archived On',
      type: TableColumnType.TEXT,
      key: 'archivedAt',
      sortable: true
    },
    {
      id: 3,
      header: 'Creation Date',
      type: TableColumnType.TEXT,
      key: 'createdAt',
      sortable: true
    },
    {
      id: 4,
      header: 'Created By',
      type: TableColumnType.USER,
      key: 'creatorName',
      sortable: true
    }
  ];

  const studyStore = useStudyStore();
  const formStore = useFormStore();
  const archives = ref<ArchiveModel[]>();

  const archiveLoader = ref(new LoadingStateModel({isLoading: false}));
  const getArchivedCases = async () => {
    archiveLoader.value.startLoading();
    try {
      archives.value = [];
      const casesRes = await CaseService.getStudyCases(studyStore.selectedStudyId!, true);
      casesRes.cases.forEach(caseItem => archives.value!.push(ArchiveModel.mapWithCase(caseItem)));

      const entriesRes = await EntryService.getArchivedEntries(studyStore.selectedStudyId!);
      entriesRes.entries.forEach(entry => {
        const entryCase = casesRes.cases.find(caseItem => caseItem.identifier === entry.caseIdentifier);
        if (!entryCase) {
            archives.value!.push(ArchiveModel.mapWithEntry(entry, formStore.formList));   
        }
      });
    } catch (error) {
      archiveLoader.value.setError(error);
    } finally {
      archiveLoader.value.stopLoadingAndFinish();
    }
  };

  const restoreResources = async (resources: ArchiveModel[], status: string) => {
    archiveLoader.value.startLoading();
    try {
      const postPayload: PostLockUnlockResource = {
        responses: resources.filter(resource => resource.entry).map(entryItem => ({
          identifier: entryItem.identifier,
          revisionIdentifier: entryItem.identifier
        })),
        cases: resources.filter(resource => resource.caseItem).map(caseItem => ({
          identifier: caseItem.identifier,
          revisionIdentifier: caseItem.identifier
        })),
        status
      };
      await StudyService.archiveResources(studyStore.selectedStudyId!, postPayload);
      let toastMsg = 'Selected resources have been restored';
      if (status === 'delete') {
        toastMsg = 'Selected resources have been deleted';
      }
      archiveLoader.value.setSuccess(toastMsg);
      getArchivedCases();
    } catch (error) {
      archiveLoader.value.setError(error);
    } finally {
      archiveLoader.value.stopLoadingAndFinish();
    }
  };

  return {
    getArchivedCases,
    restoreResources,
    tableColumns,
    archives,
    archiveLoader
  };
});
